"use client";

import { useFormContext } from "react-hook-form";
import { parsePhoneNumberFromString, type CountryCode } from "libphonenumber-js";
import { Input } from "@/components/ui/input";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { AvatarUpload } from "@/components/ui/avatar-upload";
import { PhoneInput, getFullPhoneNumber } from "@/components/ui/phone-input";
import { TimezoneSelect } from "@/components/ui/timezone-select";

export function StepAboutYou() {
  const { control, watch, setValue } = useFormContext();
  const phoneCountryCode = watch("about.phone_country_code");

  return (
    <>
      <AvatarUpload
        imageUrl={watch("about.avatar") || null}
        onImageChange={(url) => setValue("about.avatar", url)}
        onImageRemove={() => setValue("about.avatar", null)}
        label="Profile Picture"
        optional
        placeholderIcon="IconPeopleCircle"
      />

      <div className="flex gap-3">
        <FormField
          control={control}
          name="about.first_name"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormLabel required>First name</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="about.last_name"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormLabel required>Last name</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={control}
        name="about.phone_number"
        render={({ field }) => (
          <FormItem>
            <FormLabel optional>Phone Number</FormLabel>
            <FormControl>
              <PhoneInput
                key={phoneCountryCode || "US"}
                defaultCountry={phoneCountryCode || "US"}
                onCountryChange={(country) => {
                  if (country) {
                    setValue("about.phone_country_code", country as CountryCode);
                  }
                }}
                value={
                  field.value
                    ? getFullPhoneNumber(phoneCountryCode, field.value)
                    : undefined
                }
                onChange={(value) => {
                  if (!value) {
                    field.onChange("");
                    return;
                  }

                  const parsed = parsePhoneNumberFromString(value);
                  if (parsed?.country) {
                    setValue("about.phone_country_code", parsed.country as CountryCode);
                    field.onChange(parsed.formatNational());
                    return;
                  }

                  field.onChange(value);
                }}
                onBlur={field.onBlur}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="about.timezone"
        render={({ field }) => (
          <FormItem>
            <FormLabel required>Account Timezone</FormLabel>
            <FormControl>
              <TimezoneSelect value={field.value} onChange={field.onChange} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="grid gap-2">
        <Label className="leading-5 font-normal text-heading">Account Email</Label>
        <Input variant="readonly" readOnly value={watch("about.email")} tabIndex={-1} />
      </div>
    </>
  );
}
